/* eslint-disable max-len */

import { Popover } from 'antd';
import { FiTrendingUp, FiTrendingDown, FiMinus, FiClock } from 'react-icons/fi';
import dayjs from 'dayjs';
import { formatTimeHM, getResponseTimeColor } from '../../../../utils/string.utils';

import style from './index.module.less';

const ESCALATION_BANDS = [
  { max: 20, label: 'CALM', bg: '#E8F5E9', text: '#2E7D32', border: '1px solid rgb(133 188 136)' },
  { max: 40, label: 'LOW', bg: '#FFF8E1', text: '#F9A825', border: '1px solid #FBC02D' },
  { max: 60, label: 'MODERATE', bg: '#FFF3E0', text: '#EF6C00', border: '1px solid #F57C00' },
  { max: 80, label: 'HIGH', bg: '#FBE9E7', text: '#D32F2F', border: '1px solid #E53935' },
  { max: 100, label: 'CRITICAL', bg: '#D32F2F', text: '#FFFFFF', border: '1px solid #B71C1C' },
];

const getBand = (score: number) => {
  return ESCALATION_BANDS.find((b) => score <= b.max) ?? ESCALATION_BANDS[4];
};

const TrajectoryIcon = ({ trajectory }: { trajectory?: string }) => {
  switch (trajectory) {
    case 'improving':
      return <FiTrendingUp size={14} color="#2E7D32" />;
    case 'declining':
      return <FiTrendingDown size={14} color="#D32F2F" />;
    case 'stable':
    default:
      return <FiMinus size={14} color="#999" />;
  }
};

const ResponseTimeClock = ({ averageMinutes }: { averageMinutes?: number }) => {
  const color = getResponseTimeColor(averageMinutes);

  if (!color) return null;

  const content = (
    <span className={style.responseTimeValue} style={{ color }}>{formatTimeHM(Number(averageMinutes))}</span>
  );

  return (
    <Popover
      content={content}
      trigger="hover"
      placement="top"
      destroyTooltipOnHide
    >
      <FiClock size={14} color={color} strokeWidth={3} />
    </Popover>
  );
};

const HoverCardContent = ({ item }: { item: any }) => {
  const score = item.escalation_score ?? 0;
  const band = getBand(score);
  const analysis = item.escalation_analysis;
  const concerns: string[] = analysis?.key_concerns ?? [];
  const visibleConcerns = concerns.slice(0, 3);
  const remaining = concerns.length - 3;
  const flagCount = analysis?.message_flags?.length ?? analysis?.message_flags ?? 0;

  return (
    <div className={style.hoverCard}>
      <div className={style.header}>
        <span className={style.title}>Escalation Risk</span>
        <div className={style.headerRight}>
          <span className={style.score}>{score}/100</span>
          <span
            className={style.badge}
            style={{ backgroundColor: band.bg, color: band.text, border: band.border }}
          >
            {band.label}
          </span>
        </div>
      </div>

      {analysis?.summary && (
        <div className={style.summary}>{analysis.summary}</div>
      )}

      <div className={style.metricsGrid}>
        <span className={style.metricLabel}>Sentiment</span>
        <span className={style.metricValue}>
          {analysis?.sentiment_trajectory ?? '—'}
          {analysis?.sentiment_trajectory && <TrajectoryIcon trajectory={analysis?.sentiment_trajectory} />}
        </span>

        <span className={style.metricLabel}>Intervention</span>
        <span className={style.metricValue}>
          {analysis?.manager_intervention_likelihood ?? '—'}
        </span>

        <span className={style.metricLabel}>Comments</span>
        <span className={style.metricValue}>
          {analysis?.comments_analyzed ?? 0} analyzed
        </span>
      </div>

      {visibleConcerns.length > 0 && (
        <div className={style.concernsSection}>
          <div className={style.concernsTitle}>Key Concerns</div>
          <ul className={style.concernsList}>
            {visibleConcerns.map((concern, i) => (
              <li key={i}>{concern}</li>
            ))}
          </ul>
          {remaining > 0 && (
            <div className={style.concernsMore}>+{remaining} more</div>
          )}
        </div>
      )}

      <div className={style.footer}>
        <div>Flag: {flagCount} flagged messages</div>
        {item.escalation_analyzed_at && (
          <div>Analyzed {dayjs(item.escalation_analyzed_at).fromNow()}</div>
        )}
        {item.response_time?.average_minutes != null && (
          <>
            <div className={style.responseTimeSeparator} />
            <div className={style.responseTimeContainer}>
              <span className={style.responseTimeLabel}>Response time (avg):</span>
              <span
                className={style.responseTimeValue}
                style={{ color: getResponseTimeColor(item.response_time.average_minutes) || undefined }}
              >
                {formatTimeHM(item.response_time.average_minutes)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EscalationBadge = ({ item }: { item: any }) => {
  const score = item.escalation_score ?? 0;
  const band = getBand(score);
  const averageMinutes = item.response_time?.average_minutes;

  return (
    <div style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
      <ResponseTimeClock averageMinutes={averageMinutes} />
      <Popover
        content={<HoverCardContent item={item} />}
        trigger="hover"
        placement="bottom"
        destroyTooltipOnHide
      >
        <span
          className={style.badge}
          style={{ backgroundColor: band.bg, color: band.text, border: band.border }}
        >
          {score} {band.label}
        </span>
      </Popover>
    </div>
  );
};

export default EscalationBadge;
